import { Box, Typography } from "@mui/material";
import type { SponsorCampaign } from "../campaignTypes";
import {
  PORTAL_MAX_WIDTH,
  bodySx,
  cardSurfaceSx,
  eyebrowSx,
  portalFont,
  sectionTitleSx,
} from "../partnerTheme";

export default function PlatformPerformanceTable({
  data,
}: {
  data: SponsorCampaign["platformPerformance"];
}) {
  if (!data.rows.length) return null;

  const showShare = data.showShareColumn !== false;
  const showTotal = data.showTotalRow !== false;
  const showEngagements = data.rows.some((row) => Boolean(row.engagements));
  const metricLabel = data.metricColumnLabel ?? "Impressions";

  const headers = [
    "Platform",
    metricLabel,
    ...(showEngagements ? ["Engagements"] : []),
    ...(showShare ? ["Share"] : []),
  ];

  const totalShare = data.rows.reduce((sum, row) => sum + row.sharePercent, 0);
  const totalImpressions = data.rows
    .reduce((sum, row) => sum + row.impressionsRaw, 0)
    .toLocaleString("en-US");

  const gridCols = showShare
    ? showEngagements
      ? "2fr 1fr 1fr 1.2fr"
      : "2fr 1fr 1.2fr"
    : showEngagements
      ? "2fr 1fr 1fr"
      : "2fr 1fr";

  return (
    <Box
      id="performance"
      component="section"
      sx={{
        maxWidth: PORTAL_MAX_WIDTH,
        mx: "auto",
        px: { xs: 2, md: 4 },
        py: { xs: 6, md: 10 },
        scrollMarginTop: 96,
      }}
    >
      {data.eyebrow ? <Typography sx={eyebrowSx}>{data.eyebrow}</Typography> : null}
      <Typography sx={sectionTitleSx}>{data.title}</Typography>
      {data.description ? (
        <Typography sx={{ ...bodySx, maxWidth: 720, mb: 4 }}>{data.description}</Typography>
      ) : null}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1.7fr 1fr" },
          gap: 2,
          alignItems: "stretch",
        }}
      >
        <Box sx={{ ...cardSurfaceSx, p: { xs: 2, md: 3 }, overflow: "hidden" }}>
          <Box
            sx={{
              display: { xs: "none", sm: "grid" },
              gridTemplateColumns: gridCols,
              gap: 2,
              pb: 1.5,
              mb: 1,
              borderBottom: "1px solid var(--portal-border)",
            }}
          >
            {headers.map((label) => (
              <Typography
                key={label}
                sx={{
                  fontFamily: portalFont,
                  fontSize: "0.6875rem",
                  fontWeight: 700,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  color: "var(--portal-text-muted)",
                }}
              >
                {label}
              </Typography>
            ))}
          </Box>

          {data.rows.map((row) => (
            <Box
              key={row.id}
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: gridCols },
                gap: { xs: 1, sm: 2 },
                py: 2,
                borderBottom: "1px solid var(--portal-border)",
                alignItems: "center",
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontFamily: portalFont,
                    fontWeight: 700,
                    color: "var(--portal-text)",
                    mb: 0.35,
                  }}
                >
                  {row.platform}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: portalFont,
                    fontSize: "0.8125rem",
                    color: "var(--portal-text-muted)",
                  }}
                >
                  {row.contentNote}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontFamily: portalFont,
                  fontWeight: 700,
                  color: "var(--portal-text)",
                  fontSize: "1.05rem",
                }}
              >
                {row.impressions}
              </Typography>
              {showEngagements ? (
                <Typography
                  sx={{
                    fontFamily: portalFont,
                    fontWeight: 700,
                    color: "var(--portal-text)",
                    fontSize: "1.05rem",
                  }}
                >
                  {row.engagements || "—"}
                </Typography>
              ) : null}
              {showShare ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                  <Box
                    sx={{
                      flex: 1,
                      height: 6,
                      borderRadius: 999,
                      bgcolor: "rgba(255,255,255,0.08)",
                      overflow: "hidden",
                      minWidth: 64,
                    }}
                  >
                    <Box
                      sx={{
                        width: `${Math.min(100, Math.max(0, row.sharePercent))}%`,
                        height: "100%",
                        bgcolor: "var(--portal-accent)",
                        borderRadius: 999,
                      }}
                    />
                  </Box>
                  <Typography
                    sx={{
                      fontFamily: portalFont,
                      fontWeight: 600,
                      color: "var(--portal-text)",
                      minWidth: 40,
                      fontSize: "0.875rem",
                    }}
                  >
                    {row.sharePercent}%
                  </Typography>
                </Box>
              ) : null}
            </Box>
          ))}

          {showTotal ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: gridCols },
                gap: 2,
                pt: 2,
                alignItems: "center",
              }}
            >
              <Typography sx={{ fontFamily: portalFont, fontWeight: 700, color: "var(--portal-text)" }}>
                Total
              </Typography>
              <Typography sx={{ fontFamily: portalFont, fontWeight: 700, color: "var(--portal-text)" }}>
                {totalImpressions}
              </Typography>
              {showEngagements ? <Box /> : null}
              {showShare ? (
                <Typography sx={{ fontFamily: portalFont, fontWeight: 700, color: "var(--portal-text)" }}>
                  {totalShare}%
                </Typography>
              ) : null}
            </Box>
          ) : null}
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box
            sx={{
              ...cardSurfaceSx,
              flex: 1,
              p: 3,
              bgcolor: "color-mix(in srgb, var(--portal-accent) 14%, var(--portal-surface))",
              borderColor: "color-mix(in srgb, var(--portal-accent) 35%, transparent)",
            }}
          >
            <Typography
              sx={{
                fontFamily: portalFont,
                fontSize: "0.6875rem",
                fontWeight: 700,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "var(--portal-text-muted)",
                mb: 1.5,
              }}
            >
              {data.totalImpressionsLabel}
            </Typography>
            <Typography
              sx={{
                fontFamily: portalFont,
                fontWeight: 700,
                fontSize: "clamp(2.25rem, 3vw + 1rem, 3.25rem)",
                letterSpacing: "-0.03em",
                color: "var(--portal-text)",
              }}
            >
              {data.totalImpressionsValue}
            </Typography>
          </Box>
          {data.secondaryStat ? (
            <Box sx={{ ...cardSurfaceSx, flex: 1, p: 3 }}>
              <Typography
                sx={{
                  fontFamily: portalFont,
                  fontSize: "0.6875rem",
                  fontWeight: 700,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: "var(--portal-text-muted)",
                  mb: 1.5,
                }}
              >
                {data.secondaryStat.label}
              </Typography>
              <Typography
                sx={{
                  fontFamily: portalFont,
                  fontWeight: 700,
                  fontSize: "clamp(2.25rem, 3vw + 1rem, 3.25rem)",
                  letterSpacing: "-0.03em",
                  color: "var(--portal-text)",
                  mb: 1.5,
                }}
              >
                {data.secondaryStat.value}
              </Typography>
              <Typography sx={{ ...bodySx, fontSize: "0.8125rem" }}>
                {data.secondaryStat.detail}
              </Typography>
            </Box>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}
